import BugReportIcon from '@mui/icons-material/BugReport'
import { Box, Button, Chip, IconButton, Modal, Paper, Stack, TextField, Typography } from '@mui/material'
import { addEdge, applyEdgeChanges, applyNodeChanges, Background, BackgroundVariant, Controls, Panel, ReactFlow, useReactFlow, ViewportPortal, Connection as XYConnection, Edge as XYEdge, EdgeChange as XYEdgeChange, Node as XYNode, NodeChange as XYNodeChange } from '@xyflow/react'
import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import LocalCache from '../../backend/LocalCache'
import affirm from '../../core/Affirm'
import algo from '../../core/Algo'
import { IApprovalRequest, statusOptions } from '../../definitions/Approval'
import { IFlow } from '../../definitions/Nodes'
import { IExecutionEnvironment, IFlowRunConfig, ISimpleExecutionEnvironment } from '../../definitions/NodesExecution'
import Helper from '../../helpers/Helper'
import FlowExecutionManager from '../../managers/FlowExecutionManager'
import FlowUIManager, { IFlowChange } from '../../managers/FlowUIManager'
import ValidationManager from '../../managers/ValidationManager'
import { useAppSelector } from '../../store/Hooks'
import JSONEditor from '../editor/JSONEditor'
import useCommand from '../hooks/useCommand'
import { snackbar } from '../services/SnackbarService'
import CustomEdgeUI from './edges/CustomEdgeUI'
import FlowFloatingActionBar from './editor/FlowFloatingActionBar'
import FlowSubmitModal from './editor/FlowSubmitModal'
import ErrorsNodePopover from './ErrorsNodePopover'
import ExecutionEnvironmentCardInfo from './ExecutionEnvironmentCardInfo'
import FlowBorderManager from './FlowBorderManager'
import { useDnD } from './hooks/DnDContext'
import NodeDebugUiInfo from './NodeDebugUiInfo'
import NodeDetailPanel from './NodeDetailPanel'
import CustomNodeUI from './nodes/CustomNodeUI'
import RenderTableSchema from './nodes/RenderTableSchema'
import NodesMenu from './nodes_menu/NodesMenu'

const NODE_TYPES = {
    custom: CustomNodeUI
}
const EDGE_TYPES = {
    custom: CustomEdgeUI
}

export type FlowEditorMode = 'edit' | 'run' | 'dry-run' | 'audit' | 'compare'

interface P {
    initialFlow: IFlow
    initialExecEnv: IExecutionEnvironment
    initialEditorMode: FlowEditorMode
    flowRunConfig?: IFlowRunConfig
    approval: IApprovalRequest
}

function SettlementFlowEditor({ initialFlow, initialExecEnv, initialEditorMode, flowRunConfig, approval }: P) {
    const mode = useAppSelector(s => s.app.mode)
    const debugMode = useAppSelector(s => s.session.debugMode)
    const { screenToFlowPosition } = useReactFlow()
    const [type] = useDnD()
    const [flow, setFlow] = useState<IFlow>(initialFlow)
    const [editorMode, setEditorMode] = useState<FlowEditorMode>(initialEditorMode ?? 'edit')
    const [execEnv, setExecEnv] = useState<IExecutionEnvironment>(initialExecEnv)
    const [comparedExecEnv, setComparedExecEnv] = useState<ISimpleExecutionEnvironment>(null)
    const [openDebugModal, setOpenDebugModal] = useState(false)
    const [openFlowSubmitModal, setOpenFlowSubmitModal] = useState(false)

    // const initial = FlowUIManager.flowToUI(flow)
    const [nodes, setNodes] = useState([])
    const [edges, setEdges] = useState([])

    const [showSchemaOnEdge, setShowSchemaOnEdge] = useState({ visible: false, error: false, content: null, position: { x: 0, y: 0 } })
    const timerShowSchemaOnEdge = useRef(null)
    const showSchemaOnEdgeRef = useRef(null)
    const [showNodeDesc, setShowNodeDesc] = useState({ visible: false, position: { x: 0, y: 0 }, content: null })
    const timerShowNodeDesc = useRef(null)

    // const queue = useRef(new Queue<IFlowChange>())
    const canEdit = (approval.status === 'draft' || approval.status === 'rejected') && editorMode === 'edit'

    const saveDebounceMS = 300
    const onExecuteSave = useCallback(_.debounce((lastFlowUpdate: IFlow) => execute(lastFlowUpdate), saveDebounceMS), [])

    const { execute, status: statusUpdateFlow } = useCommand({
        commandFn: (newFlow: IFlow) => {
            const updatedFlow = ApiClient.Flow.updateFlow(newFlow)
            return updatedFlow
        },
        onFinish: result => {
            if (result.status === 'success') {
                // TODO VERY IMPORTANT!!!!
                // I'm making a very bold assumption that if the data I pass to it is what comes back to me then.
                // The problem is that by setting the setFlow after the call to the backend with the result, if in the meantime another change has been made this is cancelled.
                // In this way by putting the flow value (update Flow) before executing the call I don't have this problem, but if for some reason the backend fails or does different logic compared to just saving the data then the behavior is wrong.
                // The problem needs to be solved properly.

                // setFlow(result.response)
                LocalCache.set('get-flow-' + result.response.uuid, result.response)
            } else snackbar.show({ severity: 'error', text: result.errorText })
            // queue.current.clear()
        }
    })

    useEffect(() => {
        setFlow(initialFlow)
        setExecEnv(initialExecEnv)
        setEditorMode(initialEditorMode)

        const initial = FlowUIManager.flowToUI(initialFlow)
        setNodes(initial.nodes)
        setEdges(initial.edges)
    }, [initialFlow, initialExecEnv, initialEditorMode])

    useEffect(() => {
        return () => {
            FlowExecutionManager.stopFlowExecution(flow)
        }
    }, [])

    useEffect(() => {
        if (flowRunConfig) {
            if (flowRunConfig?.dryRunConfig?.samplePercentage)
                setEditorMode('dry-run')
            else
                setEditorMode('run')
        }
    }, [])

    const applyFlowChange = useCallback((change: IFlowChange) => {
        try {
            const updatedFlow = FlowUIManager.flowToUI(change.flow)
            // monkey performace, it updates all the nodes and edges
            if (change.isUpdated) {
                setNodes(updatedFlow.nodes)
                setEdges(updatedFlow.edges)
            }
            updateFlow(change)
        } catch (error) {
            console.error(error)
            snackbar.show({ severity: 'error', text: error.message })
        }
    }, [])

    const onDragOver = useCallback(event => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
    }, [])

    const updateFlow = useCallback((change: IFlowChange) => {
        if (!canEdit) return

        try {
            // TODO To be reviewed: Previous test: Put changes inside one thing and then take the last one
            // queue.current.enqueue(change)
            // queue.current.peekLast().flow

            if (change.isUpdated) setFlow(change.flow)
            if (change.needsSync) onExecuteSave(change.flow)
        } catch (error) {
            console.error(error)
            snackbar.show({ severity: 'error', text: error.message })
        }
    }, [])

    const onDrop = useCallback(event => {
        affirm(event, 'Drag event is not found')
        affirm(type, 'Invalid type')
        event.preventDefault()

        try {
            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY
            })

            const update = FlowUIManager.createNode(type, position, flow)

            const newNode: XYNode = {
                id: update.newNode.uuid,
                position: update.newNode.coords,
                type: 'custom',
                data: { node: update.newNode }
            }

            const addNodes = FlowUIManager.updateNodes([{ item: newNode.data, type: 'add' }], flow)
            applyFlowChange(addNodes)
        } catch (error) {
            console.error(error)
            snackbar.show({ severity: 'error', text: error.message })
        }
    }, [screenToFlowPosition, type, flow])

    const autoLayout = useCallback(() => {
        try {
            const layouted = FlowUIManager.autoLayout(nodes, edges)
            setNodes([...layouted.nodes])
            setEdges([...layouted.edges])
            const update = FlowUIManager.updateNodes(layouted.nodes.map(x => ({ type: 'position', position: x.position, id: x.id })), flow)
            applyFlowChange(update)
        } catch (error) {
            console.error(error)
            snackbar.show({ severity: 'error', text: error.message })
        }
    }, [nodes, edges, flow])

    const onNodesChange = useCallback((changes: XYNodeChange<any>[]) => {
        try {
            setNodes(prev => applyNodeChanges(changes, prev))
            const update = FlowUIManager.updateNodes(changes, flow)
            applyFlowChange(update)
        } catch (error) {
            console.error(error)
            snackbar.show({ severity: 'error', text: error.message })
        }
    }, [flow])

    const onEdgesChange = useCallback((changes: XYEdgeChange<XYEdge>[]) => {
        try {
            setEdges(eds => applyEdgeChanges(changes, eds))
            const update = FlowUIManager.updateConnections(changes, flow)
            applyFlowChange(update)
        } catch (error) {
            console.error(error)
            snackbar.show({ severity: 'error', text: error.message })
        }
    }, [flow])

    const onConnect = useCallback((params: XYEdge | XYConnection) => {
        try {
            setEdges(prev => addEdge(params, prev))
            const update = FlowUIManager.createConnection(params, flow)
            // TODO: we need to optimize this, we are updating all the flow!
            applyFlowChange(update)
        } catch (error) {
            console.error(error)
            snackbar.show({ severity: 'error', text: error.message })
        }
    }, [flow])

    const isValidConnection = useCallback((edge: XYEdge | XYConnection) => {
        try {
            affirm(edge, 'Invalid edge')
            affirm(edge.source, 'Invalid @edge source')
            affirm(edge.target, 'Invalid @edge target')

            const source = flow.nodes.find(x => x.uuid === edge.source)
            const target = flow.nodes.find(x => x.uuid === edge.target)

            affirm(source, `Source node ${edge.source} doesn't exist in flow`)
            affirm(target, `Target node ${edge.target} doesn't exist in flow`)

            const errors = ValidationManager.validateNewConnection(source, target, edge.sourceHandle, edge.targetHandle, flow)
            if (errors.length > 0) return false

            return true
        } catch (error) {
            console.error(error)
            snackbar.show({ severity: 'error', text: error.message })
            return false
        }
    }, [flow])

    const onEdgeMouseEnter = useCallback((event: React.MouseEvent, edge: XYEdge) => {
        const convertedPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY })
        // @ts-ignore
        const metadata = flow.nodes.find(n => n.uuid === edge.source).type === 'exe_subFlow' ? flow.nodes.find(n => n.uuid === edge.source).jsonSchema.value : flow.nodes.find(n => n.uuid === edge.source).jsonSchema
        if (!metadata) {
            timerShowSchemaOnEdge.current = setTimeout(() => {
                setShowSchemaOnEdge({
                    ...showSchemaOnEdge,
                    visible: true,
                    content: null,
                    error: true,
                    position: { ...showSchemaOnEdge.position, ...convertedPosition }
                })
            }, 500)
            return
        }

        timerShowSchemaOnEdge.current = setTimeout(() => {
            setShowSchemaOnEdge({
                ...showSchemaOnEdge,
                visible: true,
                content: metadata,
                error: false,
                position: { ...showSchemaOnEdge.position, ...convertedPosition }
            })
        }, 500)
    }, [showSchemaOnEdge, setShowSchemaOnEdge, flow])

    const onEdgeMouseLeave = useCallback(() => {
        if (!showSchemaOnEdgeRef.current) {
            clearTimeout(timerShowSchemaOnEdge.current)
            timerShowSchemaOnEdge.current = null
        }
    }, [flow])

    const onNodeMouseEnter = useCallback((_, node: XYNode) => {
        clearTimeout(timerShowNodeDesc.current)
        timerShowNodeDesc.current = null
        const desc = flow.nodes.find(n => n.uuid === node.id)?.description ?? null
        setShowNodeDesc({ position: { x: node.position.x + node.measured?.width + 10, y: node.position.y }, visible: true, content: desc })
    }, [flow])

    const onNodeMouseLeave = useCallback(() => {
        timerShowNodeDesc.current = setTimeout(() => {
            setShowNodeDesc({ ...showNodeDesc, visible: false })
        }, 100)
    }, [flow])

    return (
        <FlowBorderManager mode={editorMode} flow={flow}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                edgeTypes={EDGE_TYPES}
                zoomOnDoubleClick={false}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={canEdit ? onConnect : null}
                onDragOver={onDragOver}
                nodesDraggable={canEdit}
                onDrop={onDrop}
                deleteKeyCode={canEdit && ['Backspace', 'Delete']}
                isValidConnection={isValidConnection}
                colorMode={mode === 'dark' ? 'dark' : 'light'}
                proOptions={{ hideAttribution: true }}
                snapGrid={[16, 16]}
                snapToGrid
                onEdgeMouseEnter={onEdgeMouseEnter}
                onEdgeMouseLeave={onEdgeMouseLeave}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                fitView>
                <Controls position='bottom-right' />
                <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
                <NodeDetailPanel
                    flow={flow}
                    isLoading={statusUpdateFlow === 'loading'}
                    applyFlowChange={applyFlowChange}
                    editorMode={editorMode}
                    execEnv={execEnv}
                    comparedExecEnv={comparedExecEnv}
                    approval={approval}
                />
                <Panel position='bottom-center'>
                    <FlowFloatingActionBar
                        flow={flow}
                        setFlow={setFlow}
                        nodes={nodes}
                        execEnv={execEnv}
                        comparedExecEnv={comparedExecEnv}
                        editorMode={editorMode}
                        setEditorMode={setEditorMode}
                        applyFlowChange={applyFlowChange}
                        autoLayout={autoLayout}
                        setExecEnv={setExecEnv}
                        setComparedExecEnv={setComparedExecEnv}
                        updateFlow={updateFlow}
                        statusUpdateFlow={statusUpdateFlow}
                        flowRunConfig={flowRunConfig}
                        approval={approval}
                    />
                </Panel>
            </ReactFlow>

            {showSchemaOnEdge.visible && (
                <ViewportPortal>
                    <Box
                        ref={showSchemaOnEdgeRef}
                        className='nodrag nowheel nopan'
                        onMouseLeave={() => {
                            clearTimeout(timerShowSchemaOnEdge.current)
                            timerShowSchemaOnEdge.current = null
                            setShowSchemaOnEdge({ ...showSchemaOnEdge, visible: false })
                        }}
                        style={{
                            pointerEvents: 'all',
                            zIndex: 2000,
                            transform: `translate(${showSchemaOnEdge.position.x - 30}px, ${showSchemaOnEdge.position.y - 30}px)`,
                            position: 'absolute',
                            padding: 'var(--unit)',
                            borderRadius: 'var(--unit)'
                        }}>
                        {showSchemaOnEdge.error
                            ? (
                                    <Paper
                                        sx={{
                                            backgroundColor: '#F4433629',
                                            borderColor: '#F4433680',
                                            padding: 1,
                                            width: 250,
                                            backdropFilter: 'blur(calc(var(--unit) / 4))',
                                            boxShadow: '0px 0px 10px 0px #0000001A'

                                        }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography color='error' fontWeight='bold'>Warning</Typography>
                                            <Typography variant='caption'>You must recompile after making any change to this flow</Typography>
                                        </Box>
                                    </Paper>
                                )
                            : <RenderTableSchema data={showSchemaOnEdge.content} maxHeight={300} />}
                    </Box>
                </ViewportPortal>
            )}

            {showNodeDesc.visible && showNodeDesc?.content && (
                <ViewportPortal>
                    <Paper
                        onMouseEnter={() => {
                            clearTimeout(timerShowNodeDesc.current)
                            timerShowNodeDesc.current = null
                        }}
                        onMouseLeave={() => setShowNodeDesc({ ...showNodeDesc, visible: false })}
                        className='nodrag nowheel nopan'
                        sx={{
                            left: showNodeDesc.position.x,
                            top: showNodeDesc.position.y,
                            position: 'absolute',
                            pointerEvents: 'all',
                            zIndex: 2000,
                            padding: 2,
                            borderRadius: 'var(--unit)',
                            backgroundColor: mode === 'dark' ? '#29282869' : '#ffffff75',
                            borderColor: '#75756b80',
                            maxWidth: 250,
                            maxHeight: 300,
                            backdropFilter: 'blur(calc(var(--unit) / 2))',
                            boxShadow: '0px 0px 10px 0px #0000001A',
                            overflow: 'auto'
                        }}>
                        <Typography variant='caption'>
                            {showNodeDesc.content}
                        </Typography>
                    </Paper>
                </ViewportPortal>
            )}

            {canEdit && (
                <Panel position='top-left'>
                    <Box sx={{ display: 'flex', gap: 2, position: 'absolute', top: 10, left: 10, alignItems: 'center' }}>
                        <NodesMenu flow={flow} applyFlowChange={applyFlowChange} />
                        {(Helper.isDev() || debugMode) && (
                            <>
                                <IconButton onClick={() => setOpenDebugModal(true)} color='primary'>
                                    <BugReportIcon />
                                </IconButton>
                                <NodeDebugUiInfo />
                            </>
                        )}
                    </Box>
                </Panel>
            )}
            {(execEnv && (editorMode === 'audit' || editorMode === 'compare')) && (
                <Box sx={{ position: 'absolute', bottom: 0, right: 40, padding: 2, zIndex: 9, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <ExecutionEnvironmentCardInfo executionEnviroment={execEnv} />
                    {comparedExecEnv && <ExecutionEnvironmentCardInfo executionEnviroment={comparedExecEnv} isCompared />}
                </Box>
            )}
            {
                (Helper.isDev() || debugMode) && (
                    <Modal open={openDebugModal} onClose={() => setOpenDebugModal(false)} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Paper sx={{ display: 'flex', flexDirection: 'column', width: '50vw', height: '50vh', padding: 2, overflow: 'hidden' }}>
                            <Stack spacing={1} overflow='hidden' flex={1}>
                                <Typography>Flow info:</Typography>
                                <Stack overflow='hidden' flex={1}>
                                    <JSONEditor value={flow} readOnly />
                                </Stack>
                            </Stack>
                        </Paper>
                    </Modal>
                )
            }
            <>
                <FlowSubmitModal approval={approval} flow={flow} open={openFlowSubmitModal} setOpen={setOpenFlowSubmitModal} />
                <Panel position='top-right'>
                    <Stack flexDirection='row' alignItems='center' maxWidth={600}>
                        <TextField
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'transparent'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: canEdit ? 'dimgray' : 'transparent'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: canEdit ? 'dimgray' : 'transparent'
                                    },
                                    '&.Mui-disabled fieldset': {
                                        borderColor: 'transparent'
                                    },
                                    '& input': {
                                        padding: 'var(--unit)'
                                    }
                                },
                                'width': 'fit-content'
                            }}
                            value={flow.name}
                            variant='outlined'
                            disabled={!canEdit}
                            onChange={e => {
                                setFlow(prev => {
                                    const newFlow = { ...prev, name: e.target.value }
                                    return newFlow
                                })
                            }}
                            onBlur={() => updateFlow({ flow, isUpdated: true, needsSync: true })}
                        />
                        <IconButton size='small' sx={{ marginRight: 2 }}>
                            <Typography>
                                v{flow?.versioning?.version ?? 1}
                            </Typography>
                        </IconButton>
                        <Chip
                            sx={{
                                borderRadius: '999px',
                                background: 'var(--rpv-core__inner-page-background-color)',
                                fontWeight: 600,
                                mr: 2
                            }}
                            label={(
                                <Box display='flex' alignItems='center' gap={1}>
                                    <Box
                                        width={8}
                                        height={8}
                                        borderRadius='50%'
                                        bgcolor={
                                            (approval.status === 'approved' && flow.versioning.isHead)
                                                ? 'success.main'
                                                : (approval.status === 'approved' && !flow.versioning.isHead)
                                                        ? 'grey.500'
                                                        : approval.status === 'ready-for-review'
                                                            ? 'info.main'
                                                            : approval.status === 'rejected'
                                                                ? 'red'
                                                                : 'orange'
                                        }
                                    />
                                    <Typography variant='body2'>
                                        {statusOptions.find(option => option.value === approval.status)?.label}
                                    </Typography>
                                </Box>
                            )}
                            variant='outlined'
                            size='medium'
                        />
                        <Button variant='contained' onClick={() => setOpenFlowSubmitModal(true)}>Manage</Button>
                    </Stack>
                </Panel>
                <Panel position='top-right' style={{ top: 100 }}>
                    {algo.hasVal(execEnv) && execEnv?.errors.length > 0
                        ? (
                                <ErrorsNodePopover
                                    errors={execEnv.errors}
                                    nodes={flow.nodes}
                                />
                            )
                        : null}
                </Panel>
            </>
        </FlowBorderManager>
    )
}

export default SettlementFlowEditor
